import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";

export interface MercureConfig {
  hubUrl: string;
  topic: string;
  onMessage: (data: any) => void;
  onError?: (error: Error) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

export interface MercureConnection {
  connect: (token: string) => Promise<void>;
  disconnect: () => void;
  isConnected: () => boolean;
}

/**
 * Fetch Mercure JWT token from the backend
 */
export const fetchMercureToken = async (): Promise<string> => {
  try {
    const response = await apiService.get<any>(API_ENDPOINTS.MERCURE.GET_TOKEN);

    // Handle different possible response structures
    const token =
      typeof response === "string"
        ? response
        : response?.token || response?.mercureToken || response?.data?.token;

    if (!token) {
      throw new Error("Invalid response: token not found");
    }

    return token;
  } catch (error) {
    console.error("Failed to fetch Mercure token:", error);
    throw error;
  }
};

/**
 * Create a Mercure connection instance
 */
export const createMercureConnection = (
  config: MercureConfig,
): MercureConnection => {
  let abortController: AbortController | null = null;
  let reconnectTimer: NodeJS.Timeout | null = null;
  let reconnectAttempts = 0;
  let shouldReconnect = true;
  let currentToken: string | null = null;

  const MAX_RECONNECT_ATTEMPTS = 5;
  const BASE_RECONNECT_DELAY = 1000; // 1 second

  /**
   * Calculate exponential backoff delay
   */
  const getReconnectDelay = (attempt: number): number => {
    return Math.min(BASE_RECONNECT_DELAY * Math.pow(2, attempt), 30000); // Max 30 seconds
  };

  /**
   * Attempt to reconnect with exponential backoff
   */
  const attemptReconnect = async (): Promise<void> => {
    if (!shouldReconnect || reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error(
        "Max reconnection attempts reached or reconnection disabled",
      );
      config.onError?.(new Error("Connection failed after multiple attempts"));
      return;
    }

    reconnectAttempts++;
    const delay = getReconnectDelay(reconnectAttempts - 1);

    // console.log(
    //   `Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
    // );

    reconnectTimer = setTimeout(async () => {
      try {
        // Fetch new token for reconnection
        const token = await fetchMercureToken();
        await connect(token);
      } catch (error) {
        console.error("Reconnection failed:", error);
        attemptReconnect(); // Try again
      }
    }, delay);
  };

  /**
   * Process incoming SSE stream
   */
  const processStream = async (
    reader: ReadableStreamDefaultReader<Uint8Array>,
  ): Promise<void> => {
    const decoder = new TextDecoder();

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.warn("Mercure stream closed");
          config.onDisconnected?.();

          // Attempt to reconnect
          if (shouldReconnect) {
            attemptReconnect();
          }
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.substring(6).trim();

            if (!data) continue;

            try {
              const parsed = JSON.parse(data);
              config.onMessage(parsed);
            } catch (parseError) {
              // If not JSON, pass raw data
              config.onMessage({ raw: data });
            }
          }
        }
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        console.error("Error reading Mercure stream:", error);
        config.onError?.(error);
        config.onDisconnected?.();

        // Attempt to reconnect on error
        if (shouldReconnect) {
          attemptReconnect();
        }
      }
    }
  };

  /**
   * Connect to Mercure hub
   */
  const connect = async (token: string): Promise<void> => {
    if (!token) {
      throw new Error("Token is required for Mercure connection");
    }

    // Store token for reconnection
    currentToken = token;

    // Cancel any pending reconnection
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    // Close existing connection
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    try {
      const url = new URL(config.hubUrl);
      url.searchParams.append("topic", config.topic);

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Mercure connection failed: ${response.status} ${response.statusText}`,
        );
      }

      if (!response.body) {
        throw new Error("Response body is null");
      }

      // Reset reconnect attempts on successful connection
      reconnectAttempts = 0;
      config.onConnected?.();

      // Start processing the stream
      const reader = response.body.getReader();
      await processStream(reader);
    } catch (error: any) {
      if (error.name !== "AbortError") {
        console.error("Failed to connect to Mercure:", error);
        config.onError?.(error);
        throw error;
      }
    }
  };

  /**
   * Disconnect from Mercure hub
   */
  const disconnect = (): void => {
    shouldReconnect = false;
    reconnectAttempts = 0;

    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    config.onDisconnected?.();
  };

  /**
   * Check if connected
   */
  const isConnected = (): boolean => {
    return abortController !== null && !abortController.signal.aborted;
  };

  return {
    connect,
    disconnect,
    isConnected,
  };
};
